import { useMutation, type UseMutationOptions, useQueryClient, type QueryKey, type InfiniteData } from 'react-query'

import { AlmanacFilesClient, type UpdateAlmanacFileInput } from 'api/clients/AlmanacFilesClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { reportError } from 'utils/error'
import { type Response } from 'request-dot-js'

import { filesKeys, type FilesUpdateMutationKey } from './filesKeys'
import { optimisticUpdateFileInInfiniteLists, setFileDetailCache } from './cacheUtils'
import { type AlmanacFileIndexItem, type AlmanacFilesIndexResponse } from './types'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { useCurrentWorkspaceIdWithProject } from 'hooks/useCurrentWorkspaceIdWithProject'

type IndexPage = Response<AlmanacFilesIndexResponse>
type FilesInfiniteData = InfiniteData<IndexPage>

type UseUpdateFileOptions = Omit<
  UseMutationOptions<AlmanacFileIndexItem, unknown, UpdateAlmanacFileInput, MutationContext>,
  'mutationKey' | 'mutationFn'
>

interface MutationContext {
  previousDetail?: AlmanacFileIndexItem
  previousInfinite?: Array<{ queryKey: QueryKey; data: FilesInfiniteData | undefined }>
  [key: string]: unknown
}

const applyOptimisticUpdates = (file: AlmanacFileIndexItem, updates: UpdateAlmanacFileInput) => {
  const changes: Partial<AlmanacFileIndexItem> = {}

  if (Object.prototype.hasOwnProperty.call(updates, 'title')) {
    if (updates.title !== undefined && updates.title !== null) {
      changes.title = updates.title
    }
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'post_at')) {
    changes.post_at = updates.post_at ?? undefined
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'post_status')) {
    changes.post_status = updates.post_status as PostStatusPropertyValue | undefined
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'reviewed_at')) {
    changes.reviewed_at = updates.reviewed_at ?? null
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'view_metadata')) {
    changes.view_metadata = updates.view_metadata
  }

  return Object.keys(changes).length === 0 ? file : { ...file, ...changes }
}

export const useUpdateFile = (fileKey: string, options?: UseUpdateFileOptions) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceIdWithProject()

  return useMutation<AlmanacFileIndexItem, unknown, UpdateAlmanacFileInput, MutationContext>(
    filesKeys.update(workspaceId, fileKey) as FilesUpdateMutationKey,
    async (updates) => {
      if (!workspaceId) {
        throw new Error('A workspace must be selected before updating files.')
      }

      const response = await AlmanacFilesClient.update(workspaceId, fileKey, updates)

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Failed to update file.')
      }

      return response.data.almanac_file
    },
    {
      onMutate: async (updates) => {
        if (!workspaceId) return {}

        await queryClient.cancelQueries({ queryKey: filesKeys.all(workspaceId) })

        const detailKey = filesKeys.detail(workspaceId, fileKey)
        const previousDetail = queryClient.getQueryData<AlmanacFileIndexItem>(detailKey)

        if (previousDetail) {
          const nextDetail = applyOptimisticUpdates(previousDetail, updates)
          setFileDetailCache(queryClient, workspaceId, nextDetail)
        }

        const previousInfinite = (
          queryClient.getQueriesData<FilesInfiniteData>({
            queryKey: filesKeys.partialInfinite(workspaceId),
          }) as Array<[QueryKey, FilesInfiniteData | undefined]>
        ).map(([qk, data]) => ({
          queryKey: qk,
          data,
        }))

        optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, fileKey, (f) =>
          applyOptimisticUpdates(f, updates),
        )

        const userCtx = (await options?.onMutate?.(updates)) as MutationContext | void
        return { previousDetail, previousInfinite, ...(userCtx ?? {}) }
      },

      onError: (error, variables, context) => {
        reportError(error, { severity: 'error' })

        if (context?.previousDetail && workspaceId) {
          queryClient.setQueryData(filesKeys.detail(workspaceId, fileKey), context.previousDetail)
        }

        context?.previousInfinite?.forEach(({ queryKey, data }) => {
          // Snapshots taken before the query first resolved are undefined. Writing
          // undefined back marks the query successful and fires observers' onSuccess
          // with no data (react-query v3), so only restore caches that had data.
          if (data) {
            queryClient.setQueryData(queryKey, data)
          }
        })

        options?.onError?.(error, variables, context)
      },

      onSuccess: (updatedFile, variables, context) => {
        if (workspaceId) {
          setFileDetailCache(queryClient, workspaceId, updatedFile)
          optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, fileKey, () => updatedFile)
        }

        options?.onSuccess?.(updatedFile, variables, context)
      },

      onSettled: async (data, error, variables, context) => {
        if (workspaceId) {
          await queryClient.invalidateQueries({ queryKey: filesKeys.detail(workspaceId, fileKey) })
          await queryClient.invalidateQueries({ queryKey: filesKeys.partialInfinite(workspaceId) })
        }
        await options?.onSettled?.(data, error, variables, context)
      },
    },
  )
}
